import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import Grid from '@mui/material/Grid';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { theme } from '@omnigenbiodata/ui';
import DetailRow from '../DetailRow';

const successTint = theme.palette.success.lighter;

interface LinkingSuccessPanelProps {
  participant: {
    participantRef?: string | null;
    firstName?: string | null;
    surname?: string | null;
  } | null;
  sample: {
    sampleBarcode?: string | null;
    linkDate?: string | null;
  } | null;
  onStartAgain: () => void;
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function SuccessIcon() {
  return (
    <Box
      sx={{
        width: 170,
        height: 170,
        borderRadius: '50%',
        bgcolor: successTint,
        border: 2,
        borderColor: 'success.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      aria-hidden="true"
    >
      <CheckCircleOutlined color="success" sx={{ fontSize: 112 }} />
    </Box>
  );
}

function LinkingSuccessPanel({ participant, sample, onStartAgain }: LinkingSuccessPanelProps) {
  const participantName = [participant?.firstName, participant?.surname].filter(Boolean).join(' ');

  return (
    <Grid container spacing={4} alignItems="center">
      <Grid item xs={12} md={4}>
        <Box
          sx={{
            minHeight: 320,
            borderRadius: 4,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <SuccessIcon />
        </Box>
      </Grid>

      <Grid item xs={12} md={8}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            mb: 2,
            lineHeight: 1.1,
          }}
        >
          Success!
        </Typography>

        <Typography
          variant="h5"
          component="p"
          color="text.secondary"
          sx={{
            lineHeight: 1.4,
            maxWidth: 760,
          }}
        >
          Participant and sample kit successfully linked.
        </Typography>

        <Paper
          elevation={2}
          sx={{
            mt: 4,
            border: 1,
            borderColor: 'success.main',
            bgcolor: successTint,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ height: 8, bgcolor: 'success.main' }} />

          <Box p={3}>
            <Box component="dl" sx={{ m: 0 }}>
              <DetailRow label="Participant Ref" value={participant?.participantRef} />
              <DetailRow label="Participant" value={participantName} />
              <DetailRow label="Sample Barcode" value={sample?.sampleBarcode} />

              {sample?.linkDate && <DetailRow label="Link Date" value={formatDateTime(sample.linkDate)} />}
            </Box>
          </Box>
        </Paper>

        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={<LinkOutlined />}
          onClick={onStartAgain}
          sx={{
            mt: 4,
            minHeight: 72,
            fontSize: '1.25rem',
            fontWeight: 700,
          }}
        >
          Link another sample kit
        </Button>
      </Grid>
    </Grid>
  );
}

export default LinkingSuccessPanel;
